import type { TrendWeek, WeeklyAggregate } from "./body-measurements";
import { addDays } from "./days";

// Pain entries (CONTEXT.md: "Pain entry") — one per user per date, global
// like body measurements and outside week snapshots (docs/adr/0028).

export type Symptom =
  | "bloated"
  | "constipation"
  | "diarrhoea"
  | "cramp"
  | "bubbling";

export const SYMPTOM_CATALOG: readonly { key: Symptom; label: string }[] = [
  { key: "bloated", label: "Oppblåst" },
  { key: "constipation", label: "Forstoppelse" },
  { key: "diarrhoea", label: "Diaré" },
  { key: "cramp", label: "Krampe" },
  { key: "bubbling", label: "Boblende smerte" },
];

// Fixed, never in settings: the scale must mean the same thing across any
// report period (CONTEXT.md: "Smertenivå"). Each anchor names what the pain
// stops you doing rather than how it feels, which is what keeps it from
// drifting. Index 0 is level 1.
const PAIN_LEVEL_ANCHORS: readonly string[] = [
  "Ingen smerte",
  "Merker det så vidt",
  "Litt vondt, men tenker ikke mye på det",
  "Plagsomt, men gjør alt som vanlig",
  "Vondt – vanskelig å konsentrere seg",
  "Vondt – må ta pauser fra det jeg holder på med",
  "Veldig vondt – klarer lite",
  "Så vondt at jeg må legge meg",
  "Nesten ikke til å holde ut",
  "Verst tenkelige smerte",
];

export const PAIN_LEVELS: readonly number[] = PAIN_LEVEL_ANCHORS.map(
  (_, index) => index + 1,
);

export function painLevelAnchor(level: number): string {
  return PAIN_LEVEL_ANCHORS[level - 1] ?? "";
}

export interface PainEntryData {
  date: string; // YYYY-MM-DD — duplicated from the id for range reads
  level?: number; // Smertenivå, integer 1–10; absent = not set
  symptoms: Symptom[]; // catalog order, no duplicates
  note?: string; // trimmed, non-empty when present
}

export const PAIN_ENTRY_TYPE = "pain_entry";

// 58 chars ("pain_entry:" + 36-char uuid + ":" + 10-char date), inside the
// RxDB id maxLength of 64.
export function painEntryId(userId: string, date: string): string {
  return `pain_entry:${userId}:${date}`;
}

/** Catalog order, duplicates dropped — the stored form of a symptom set. */
export function normalizeSymptoms(symptoms: Symptom[]): Symptom[] {
  return SYMPTOM_CATALOG.map((s) => s.key).filter((key) =>
    symptoms.includes(key),
  );
}

/**
 * One aggregate per trend week (index-aligned with `weeks`): the average
 * Smertenivå with that week's lowest and highest, or null for a week with no
 * level set — a gap in Smertetrend. Entries without a level never count.
 */
export function aggregatePainWeekly(
  entries: PainEntryData[],
  weeks: TrendWeek[],
): Array<WeeklyAggregate | null> {
  return weeks.map((week) => {
    const sunday = addDays(week.monday, 6);
    const levels = entries
      .filter((e) => e.date >= week.monday && e.date <= sunday)
      .map((e) => e.level)
      .filter((level): level is number => typeof level === "number");
    if (levels.length === 0) return null;
    return {
      average: levels.reduce((sum, level) => sum + level, 0) / levels.length,
      minimum: Math.min(...levels),
      maximum: Math.max(...levels),
      count: levels.length,
    };
  });
}
